import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  FaArrowRight, FaShieldAlt, FaCloud, FaExchangeAlt,
  FaChartLine, FaCreditCard, FaHandshake, FaRocket, FaCheckCircle,
  FaAward, FaGlobe, FaCogs
} from 'react-icons/fa';
import './Home.css';

const stats = [
  { value: '18+', label: 'Years of Tech Experience', icon: <FaAward /> },
  { value: '2021', label: 'Year Founded', icon: <FaRocket /> },
  { value: '10+', label: 'Technology Domains', icon: <FaGlobe /> },
  { value: '6', label: 'Core Service Areas', icon: <FaCogs /> },
];

const services = [
  {
    icon: <FaCreditCard />,
    title: 'Payment Gateway',
    desc: 'Seamless, secure payment processing solutions with multi-currency support, PCI DSS compliance, and real-time transaction monitoring.',
    link: '/services',
  },
  {
    icon: <FaHandshake />,
    title: 'Escrow Payment',
    desc: 'Trusted escrow systems that protect buyers and sellers, ensuring funds are held securely until all conditions are met.',
    link: '/services',
  },
  {
    icon: <FaExchangeAlt />,
    title: 'Reconciliation Engine',
    desc: 'Automated reconciliation tools that match transactions across multiple channels, reducing manual effort and errors.',
    link: '/services',
  },
  {
    icon: <FaChartLine />,
    title: 'Finance Applications',
    desc: 'Custom finance management platforms, lending systems, investment tools, and treasury management applications.',
    link: '/services',
  },
  {
    icon: <FaCloud />,
    title: 'Cloud Services',
    desc: 'Scalable cloud infrastructure, migration services, and managed cloud solutions optimized for fintech workloads.',
    link: '/services',
  },
  {
    icon: <FaShieldAlt />,
    title: 'Cybersecurity',
    desc: 'Enterprise-grade security frameworks, compliance management, penetration testing, and threat intelligence systems.',
    link: '/security',
  },
];

const whyUs = [
  'PCI DSS & ISO 27001 Certified Solutions',
  'End-to-End Fintech Expertise',
  '99.9% System Uptime SLA',
  'Real-time Fraud Detection & Prevention',
  'Scalable Microservices Architecture',
  'Dedicated 24/7 Technical Support',
  'Regulatory Compliance Built-in',
  'Agile & Transparent Development',
];

const industries = [
  { icon: <FaCreditCard />, name: 'Banking & Payments' },
  { icon: <FaChartLine />, name: 'Investment & Trading' },
  { icon: <FaHandshake />, name: 'Lending & Credit' },
  { icon: <FaShieldAlt />, name: 'Insurance Tech' },
  { icon: <FaExchangeAlt />, name: 'Remittance & Forex' },
  { icon: <FaCloud />, name: 'Cloud Finance' },
];

function Home() {
  return (
    <div className="home">
      <Helmet>
        <title>Techmascot Technologies | Fintech Software Development Company</title>
        <meta name="description" content="Leading Fintech Software Development Company specializing in Payment Gateway, Escrow Payment, Reconciliation, Cloud Services and Finance Applications." />
        <link rel="canonical" href="https://www.techmascot.com/" />
        <meta property="og:title" content="Techmascot Technologies | Fintech Software Development Company" />
        <meta property="og:description" content="Leading Fintech Software Development Company specializing in Payment Gateway, Escrow Payment, Reconciliation, Cloud Services and Finance Applications." />
        <meta property="og:url" content="https://www.techmascot.com/" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Techmascot Technologies Private Limited",
            "url": "https://www.techmascot.com",
            "logo": "https://www.techmascot.com/logo192.png",
            "description": "Leading Fintech Software Development Company specializing in Payment Gateway, Escrow Payment, Reconciliation, Cloud Services and Finance Applications.",
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "customer support",
              "availableLanguage": "English"
            },
            "sameAs": [
              "https://www.linkedin.com/company/techmascot"
            ]
          }
        `}</script>
      </Helmet>
      {/* Hero Banner */}
      <section className="hero">
        <div className="hero-bg">
          <div className="hero-blob blob1"></div>
          <div className="hero-blob blob2"></div>
          <div className="hero-blob blob3"></div>
          <div className="hero-grid"></div>
        </div>

        <div className="container hero-container">
          <div className="hero-content">
            <div className="hero-badge">
              <FaShieldAlt />
              <span>18+ Years of Technology Expertise</span>
            </div>
            <h1 className="hero-title">
              Building the Future of <span>Financial Technology</span>
            </h1>
            <p className="hero-desc">
              Founded in 2021 by a technology professional with 18+ years of multi-domain experience,
              Techmascot Technologies is built to deliver enterprise-grade fintech solutions —
              Payment Gateways, Escrow Systems, Reconciliation Engines, and Cloud Services.
            </p>
            <div className="hero-actions">
              <Link to="/services" className="btn-primary">
                Explore Services <FaArrowRight />
              </Link>
              <Link to="/contact" className="btn-secondary">
                Talk to Experts
              </Link>
            </div>
            <div className="hero-trust">
              <FaCheckCircle className="trust-icon" />
              <span>PCI DSS Compliant</span>
              <FaCheckCircle className="trust-icon" />
              <span>ISO 27001 Ready</span>
              <FaCheckCircle className="trust-icon" />
              <span>GDPR Compliant</span>
            </div>
          </div>

          <div className="hero-visual">
            <div className="hero-card main-card">
              <div className="card-header-row">
                <div className="card-dot green"></div>
                <div className="card-dot yellow"></div>
                <div className="card-dot red"></div>
              </div>
              <div className="card-metric">
                <span className="metric-label">Processing Capacity</span>
                <span className="metric-value">10K TPS</span>
                <span className="metric-change positive">High-throughput architecture</span>
              </div>
              <div className="mini-chart">
                {[40,65,45,80,60,90,75,95,70,85].map((h, i) => (
                  <div key={i} className="chart-bar" style={{ height: `${h}%` }}></div>
                ))}
              </div>
            </div>
            <div className="hero-card side-card card-security">
              <FaShieldAlt className="side-icon" />
              <span>256-bit Encryption</span>
              <span className="side-status">Active</span>
            </div>
            <div className="hero-card side-card card-payments">
              <FaCreditCard className="side-icon" />
              <span>Gateway Integrations</span>
              <span className="side-value">20+ APIs</span>
            </div>
          </div>
        </div>

        <div className="hero-wave">
          <svg viewBox="0 0 1440 80" preserveAspectRatio="none">
            <path d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z" fill="#FFFFFF" />
          </svg>
        </div>
      </section>

      {/* Stats */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {stats.map((s, i) => (
              <div key={i} className="stat-card">
                <div className="stat-icon">{s.icon}</div>
                <div className="stat-value">{s.value}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="services-section">
        <div className="container">
          <div className="section-header">
            <span className="tag">What We Build</span>
            <h2 className="section-title">
              Comprehensive <span>Fintech Solutions</span>
            </h2>
            <p className="section-subtitle">
              From payment infrastructure to cloud platforms, we build robust systems
              that power financial operations at scale.
            </p>
          </div>
          <div className="services-grid">
            {services.map((s, i) => (
              <div key={i} className="service-card">
                <div className="service-icon">{s.icon}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
                <Link to={s.link} className="service-link">
                  Learn More <FaArrowRight />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="why-section">
        <div className="container">
          <div className="why-inner">
            <div className="why-text">
              <span className="tag">Why Techmascot</span>
              <h2 className="section-title">
                Built for <span>Mission-Critical</span> Finance
              </h2>
              <p style={{ color: 'var(--text-gray)', marginBottom: '32px', lineHeight: 1.7 }}>
                Techmascot Technologies is founded on 18+ years of hands-on technology experience
                across diverse domains. We bring deep technical knowledge, strong engineering practices,
                and a security-first mindset to every fintech solution we build.
              </p>
              <div className="why-checklist">
                {whyUs.map((item, i) => (
                  <div key={i} className="why-item">
                    <FaCheckCircle className="why-check" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <Link to="/about" className="btn-primary" style={{ marginTop: '32px' }}>
                About Our Company <FaArrowRight />
              </Link>
            </div>
            <div className="why-visual">
              <div className="why-card why-card-main">
                <div className="why-card-icon"><FaShieldAlt /></div>
                <h4>Security First</h4>
                <p>Every product is engineered with security at its core — from design to deployment.</p>
              </div>
              <div className="why-card why-card-alt">
                <div className="why-card-icon alt"><FaCogs /></div>
                <h4>Scalable Architecture</h4>
                <p>Microservices-based systems that grow with your business needs seamlessly.</p>
              </div>
              <div className="why-card why-card-alt" style={{ marginTop: 0, marginLeft: 0 }}>
                <div className="why-card-icon alt"><FaRocket /></div>
                <h4>Rapid Delivery</h4>
                <p>Agile methodology ensuring fast time-to-market without compromising quality.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="industries-section">
        <div className="container">
          <div className="section-header">
            <span className="tag">Sectors We Serve</span>
            <h2 className="section-title">
              Industries We <span>Transform</span>
            </h2>
          </div>
          <div className="industries-grid">
            {industries.map((ind, i) => (
              <div key={i} className="industry-chip">
                <span className="ind-icon">{ind.icon}</span>
                <span>{ind.name}</span>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <Link to="/industries" className="btn-outline">
              View All Industries <FaArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="cta-section">
        <div className="cta-bg">
          <div className="cta-blob"></div>
        </div>
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Build Your Fintech Solution?</h2>
            <p>
              Let's discuss how Techmascot Technologies can accelerate your financial
              technology roadmap with secure, scalable, and compliant solutions.
            </p>
            <div className="cta-actions">
              <Link to="/contact" className="btn-primary" style={{ background: '#FFFFFF', color: 'var(--primary)', borderColor: '#FFFFFF' }}>
                Start a Project <FaArrowRight />
              </Link>
              <a href="mailto:info@techmascot.in" className="btn-secondary">
                info@techmascot.in
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
